import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import produce from 'immer';
import * as React from 'react';

import { toast } from 'sonner';
import { useCreateAccountMutation } from '../../../../queries/Admin/CreateAccount/useCreateAccountMutation';
import { useCreateInitialUserMutation } from '../../../../queries/Admin/CreateAccount/useCreateInitialUserMutation';
import { AutoCompleteSearchField } from '../../../../web/components/autocomplete/AutoCompleteSearchField';
import { Checkbox } from '../../../../web/components/Checkbox';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { SelectBox } from '../../../../web/components/SelectBox';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { useCreateAccount } from '../CreateAccountV2/hooks';
import { IndustryOptions, SystemOfRecordOptions } from '../CreateAccountV2/models';
import { styleSheet } from '../CreateAccountV2/styles';
import { getBrokerDealerOptions } from '../getBrokerDealerOptions';

export const CreateAccountV2Step1: React.FC = () => {
	const {
		account,
		adminEmailAddress,
		adminFirstName,
		adminLastName,
		displayImage,
		errorMessages,
		getBrokerDealerSelection,
		getIndustrySelection,
		getRandomImage,
		getSystemOfRecordSelection,
		hipaaEnabled,
		history,
		logger,
		midMarket,
		onAutocompleteItemSelectedSalesRep,
		onBrokerDealerChanged,
		onIndustryChanged,
		onSystemOfRecordChanged,
		phoneNumber,
		setAccount,
		setDisplayImage,
		setShowLoader,
		updateAdminEmail,
		updateCompanyName,
		updateFirstName,
		updateHipaaEnabled,
		updateLastName,
		updateMidMarket,
		updatePhoneNumber,
	} = useCreateAccount();

	const createAccountMutation = useCreateAccountMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('CreateAccount-Error', error);
			setShowLoader(false);
		},
		onSuccess: createAccountSuccess,
	});

	const createInitUserMutation = useCreateInitialUserMutation();

	const isPopulatedField = (f: string) => {
		const field = f.trim();
		return field !== null && field !== undefined && field.length > 0;
	};

	const displayErrorMessage = (message: string) => {
		toast.error(message);
	};

	const goToNext = async () => {
		if (!isPopulatedField(adminEmailAddress)) {
			return displayErrorMessage('Admin email address is required');
		} else if (!isPopulatedField(adminFirstName)) {
			return displayErrorMessage('Admin first name is required');
		} else if (!isPopulatedField(adminLastName)) {
			return displayErrorMessage('Admin last name is required');
		} else if (!account.companyName) {
			return displayErrorMessage('Company name is required');
		} else if (!account.additionalInfo?.systemOfRecord || account.additionalInfo.systemOfRecord === '') {
			return displayErrorMessage('System of Record is required');
		}
		createClick();
	};

	function createClick() {
		if (!account) {
			return;
		}
		const nextAccount = produce(account, draftAccount => {
			draftAccount.planDetails = {};
			draftAccount.planDetails.planId = 1;
			draftAccount.planDetails.billingType = Api.BillingType.Comp;
			draftAccount.planDetails.users = midMarket ? 15 : 5;
			draftAccount.features = draftAccount.features || {};
			draftAccount.features.hipaa = { enabled: hipaaEnabled };
		});
		setShowLoader(true);
		createAccountMutation.mutate({ account: nextAccount });
		createAccountMutation.variables = { account: nextAccount };
		setAccount(nextAccount);
	}

	function createAccountSuccess(nextAccount: Api.IAccount) {
		if (!nextAccount) {
			return;
		}
		setShowLoader(true);
		createInitialUser(nextAccount);
	}

	async function createInitialUser(nextAccount: Api.IAccount) {
		if (!adminEmailAddress) {
			return;
		}

		try {
			await createInitUserMutation.mutateAsync({
				account: nextAccount,
				email: adminEmailAddress,
				firstName: adminFirstName,
				lastName: adminLastName,
				phone: phoneNumber || '',
			});
			setShowLoader(false);
			history.replace({
				state: {
					account: nextAccount,
					displayImage,
				},
				pathname: `/accounts/${nextAccount.id}/create-account-chargify`,
			});
		} catch (error) {
			errorMessages.pushApiError(error);
			logger.logApiError('CreateInitialUser-Error', error);
			setShowLoader(false);
		}
	}

	React.useEffect(() => {
		if (!displayImage) {
			getRandomImage(displayImage, setDisplayImage);
		}
		return;
	}, [getRandomImage, displayImage, setDisplayImage]);

	const isCreating = createAccountMutation.isPending || createInitUserMutation.isPending;

	const renderFormContent = () => {
		if (displayImage) {
			return (
				<div className={css(styleSheet.contentContainer)}>
					<h1 className={css(styleSheet.headerTitle)}>New Account</h1>
					<div className={css(styleSheet.twoColumnLayout)}>
						<figure>
							<img width={528} height={528} src={displayImage} alt='new account image' />
						</figure>
						<div className={css(styleSheet.rightColumn)}>
							<div className={css(styleSheet.fieldContainerStandard)}>
								<div>
									<div className={css(styleSheet.formFieldLabel)}>
										Company Name <span className={css(styleSheet.requiredFieldAsterisk)}>*</span>
									</div>
									<input
										type='text'
										id='accountCompanyName'
										className={css(styleSheet.textBox)}
										value={account?.companyName ?? ''}
										onChange={updateCompanyName}
										autoFocus={true}
									/>
								</div>
							</div>
							<div className={css(styleSheet.fieldContainerStandard)}>
								<div>
									<div className={css(styleSheet.formFieldLabel)}>
										Admin Email Address <span className={css(styleSheet.requiredFieldAsterisk)}>*</span>
									</div>
									<input
										type='text'
										id='accountAdminEmail'
										placeholder='john.doe@company.com'
										className={css(styleSheet.textBox)}
										value={adminEmailAddress}
										onChange={updateAdminEmail}
										spellCheck={false}
									/>
								</div>
							</div>
							<div className={css(styleSheet.fieldContainerStandard)}>
								<div className={css(styleSheet.formFieldLabel)}>
									Admin Name <span className={css(styleSheet.requiredFieldAsterisk)}>*</span>
								</div>
								<div className={css(styleSheet.adminNameInputs)}>
									<input
										type='text'
										id='accountAdminFirstName'
										placeholder='First'
										className={css(styleSheet.textBox, styleSheet.textBoxShort)}
										value={adminFirstName}
										onChange={updateFirstName}
									/>
									<input
										type='text'
										id='accountAdminLastName'
										placeholder='Last'
										className={css(styleSheet.textBox, styleSheet.textBoxShort)}
										value={adminLastName}
										onChange={updateLastName}
									/>
								</div>
							</div>
							<div className={css(styleSheet.fieldContainerStandard)}>
								<div>
									<div className={css(styleSheet.formFieldLabel)}>Phone Number</div>
									<input
										type='tel'
										id='accountAdminPhoneNumber'
										className={css(styleSheet.textBox)}
										value={phoneNumber ?? ''}
										onChange={updatePhoneNumber}
									/>
								</div>
							</div>
							<div className={css(styleSheet.fieldContainerMultiple)}>
								<div className={css(styleSheet.fieldMultipleColumn)}>
									<div className={css(styleSheet.formFieldLabel)}>Sold By</div>
									<AutoCompleteSearchField
										anchorClassName={css(styleSheet.searchBox)}
										dropdownContentClassName={css(styleSheet.autocompleteBox)}
										initialSearchQuery={
											!!account && !!account.additionalInfo && !!account.additionalInfo.salesRepName
												? account.additionalInfo.salesRepName
												: ''
										}
										inputId='employeeSearchBox1'
										inputProps={{
											placeholder: 'Search',
										}}
										onItemSelected={onAutocompleteItemSelectedSalesRep}
										resultsLimit={5}
										type={Api.ResourceAutoCompleteViewModelType.User}
									/>
								</div>
								<div className={css(styleSheet.fieldMultipleColumn)}>
									<div className={css(styleSheet.formFieldLabel)}>Industry</div>
									<SelectBox
										contentClassName={`${css(styleSheet.dropdownMenu)}`}
										onSelectionChanged={onIndustryChanged}
										options={IndustryOptions}
										selectedOption={getIndustrySelection()}
										triggerClassName={` ${css(styleSheet.selectTrigger)}`}
									/>
								</div>
							</div>
							<div className={css(styleSheet.fieldContainerMultiple)}>
								<div className={css(styleSheet.fieldMultipleColumn)}>
									<div className={css(styleSheet.formFieldLabel)}>Broker Dealer</div>
									<SelectBox
										contentClassName={`${css(styleSheet.dropdownMenu)}`}
										filterOptions={true}
										filterPlaceHolder='Search broker dealers...'
										filterClassName={css(styleSheet.dropdownSearchBox)}
										onSelectionChanged={onBrokerDealerChanged}
										options={getBrokerDealerOptions(account?.additionalInfo?.industry)}
										selectedOption={getBrokerDealerSelection()}
										triggerClassName={` ${css(styleSheet.selectTrigger)}`}
									/>
								</div>
								<div className={css(styleSheet.fieldMultipleColumn)}>
									<div className={css(styleSheet.formFieldLabel)}>
										System of Record <span className={css(styleSheet.requiredFieldAsterisk)}>*</span>
									</div>
									<SelectBox
										contentClassName={`${css(styleSheet.dropdownMenu)}`}
										filterOptions={true}
										filterPlaceHolder='Search systems...'
										filterClassName={css(styleSheet.dropdownSearchBox)}
										onSelectionChanged={onSystemOfRecordChanged}
										options={SystemOfRecordOptions}
										selectedOption={getSystemOfRecordSelection()}
										triggerClassName={` ${css(styleSheet.selectTrigger)}`}
									/>
								</div>
							</div>
							<div className={css(styleSheet.fieldContainerCheckbox)}>
								<Checkbox
									id='accountMidMarketCheckbox'
									checked={midMarket}
									onChange={updateMidMarket}
									className={css(styleSheet.customCheckbox)}
								>
									<span className={css(styleSheet.checkboxLabel)}>Is Mid Market?</span>
								</Checkbox>
							</div>
							<div className={css(styleSheet.fieldContainerCheckbox)}>
								<Checkbox
									id='accountHipaaCheckbox'
									checked={hipaaEnabled}
									onChange={updateHipaaEnabled}
									className={css(styleSheet.customCheckbox)}
								>
									<span className={css(styleSheet.checkboxLabel)}>Requires HIPAA</span>
								</Checkbox>
							</div>

							<footer className={css(styleSheet.buttonContainer)}>
								<button
									disabled={isCreating}
									className={css(baseStyleSheet.horizontalStack, baseStyleSheet.ctaButton)}
									onClick={goToNext}
								>
									{isCreating ? <LoadingSpinner type='extra-tiny' /> : null}
									<span>Next</span>
								</button>
							</footer>
						</div>
					</div>
				</div>
			);
		}
	};
	return <div className='admin-createAccount-container'>{renderFormContent()}</div>;
};
